// OrderManagement.jsx
import React, { useEffect, useState } from "react";
import {
  FiBox,
  FiTruck,
  FiCheckCircle,
  FiXCircle,
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiEye,
  FiEdit,
  FiArrowLeft,
  FiArrowRight,
  FiAlertCircle,
  FiMail,
  FiFileText,
} from "react-icons/fi";
import "./OrderManagement.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const OrderManagement = ({ onLogout }) => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isUpdating, setIsUpdating] = useState(false);
  const [orderTypeFilter, setOrderTypeFilter] = useState("all");
  const [shippingForms, setShippingForms] = useState({});
  const [invoiceForms, setInvoiceForms] = useState({});
  const shippingOptions = [
    "AUS POST",
  ];

  // Fetch orders from API
  const fetchOrders = async () => {
    try {
      setIsLoading(true);
      // In a real implementation, we would call an admin endpoint to get all orders
      // Since we don't have one, we'll simulate with the user endpoint for demo
      const token = localStorage.getItem("adminToken");
      const response = await fetch(`${API_BASE_URL}/orders/admin`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch orders");
      }

      const data = await response.json();
      setOrders(data);
      setFilteredOrders(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // Apply filters and search
  useEffect(() => {
    let result = orders;

    if (statusFilter !== "all") {
      result = result.filter((order) => order.status === statusFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (order) =>
          order.orderNumber.toLowerCase().includes(term) ||
          order.userId.toLowerCase().includes(term) ||
          (order.transactionId &&
            order.transactionId.toLowerCase().includes(term)),
      );
    }
    if (orderTypeFilter !== "all") {
      if (orderTypeFilter === "guest") {
        result = result.filter((order) => order.isGuestOrder);
      } else if (orderTypeFilter === "collect") {
        result = result.filter((order) => order.isConfirmAndCollect);
      } else if (orderTypeFilter === "regular") {
        result = result.filter(
          (order) => !order.isGuestOrder && !order.isConfirmAndCollect,
        );
      }
    }

    setFilteredOrders(result);
    setCurrentPage(1);
  }, [statusFilter, searchTerm, orders, orderTypeFilter]);

  // Update order status
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      setIsUpdating(true);
      const token = localStorage.getItem("adminToken");

      // We'll use the existing refund endpoint pattern to update status
      const response = await fetch(`${API_BASE_URL}/orders/${orderId}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        throw new Error("Failed to update order status");
      }

      // Update local state
      setOrders(
        orders.map((order) =>
          order.id === orderId ? { ...order, status: newStatus } : order,
        ),
      );

      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const toggleShippingForm = (orderId, order) => {
    const isCurrentlyOpen = shippingForms[orderId]?.showForm;
    
    if (!isCurrentlyOpen) {
      // Opening form - pre-populate with existing data if available
      setShippingForms((prev) => ({
        ...prev,
        [orderId]: {
          ...prev[orderId],
          trackingNumber: order?.trackingNumber || prev[orderId]?.trackingNumber || "",
          shippingService: order?.shippingService || prev[orderId]?.shippingService || "",
          showForm: true,
          error: null,
          success: null,
        },
      }));
    } else {
      // Closing form
      setShippingForms((prev) => ({
        ...prev,
        [orderId]: {
          ...prev[orderId],
          showForm: false,
          error: null,
          success: null,
        },
      }));
    }
  };

  const updateShippingForm = (orderId, field, value) => {
    setShippingForms((prev) => ({
      ...prev,
      [orderId]: {
        ...prev[orderId],
        [field]: value,
      },
    }));
  };

  const submitShippingInfo = async (orderId) => {
    const form = shippingForms[orderId] || {};
    const trackingNumber = form.trackingNumber?.trim();
    const shippingService = form.shippingService;

    if (!trackingNumber || !shippingService) {
      setShippingForms((prev) => ({
        ...prev,
        [orderId]: {
          ...prev[orderId],
          error: "Please provide both tracking number and shipping provider.",
          success: null,
        },
      }));
      return;
    }

    try {
      setShippingForms((prev) => ({
        ...prev,
        [orderId]: {
          ...prev[orderId],
          loading: true,
          error: null,
          success: null,
        },
      }));

      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${API_BASE_URL}/orders/${orderId}/shipping`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ trackingNumber, shippingService }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to send shipping information");
      }

      const data = await response.json();
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === orderId
            ? {
                ...order,
                trackingNumber,
                shippingService,
                status: order.status === "Shipped" ? order.status : "Shipped",
                ...data,
              }
            : order,
        ),
      );

      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder((prev) => ({
          ...prev,
          trackingNumber,
          shippingService,
          status: prev.status === "Shipped" ? prev.status : "Shipped",
          ...data,
        }));
      }

      setShippingForms((prev) => ({
        ...prev,
        [orderId]: {
          ...prev[orderId],
          loading: false,
          error: null,
          success: "Shipping info submitted successfully.",
          showForm: false,
        },
      }));
    } catch (err) {
      setShippingForms((prev) => ({
        ...prev,
        [orderId]: {
          ...prev[orderId],
          loading: false,
          error: err.message,
          success: null,
        },
      }));
    }
  };

  const toggleInvoiceForm = (orderId, order) => {
    const isCurrentlyOpen = invoiceForms[orderId]?.showSendForm;

    if (!isCurrentlyOpen) {
      setInvoiceForms((prev) => ({
        ...prev,
        [orderId]: {
          ...prev[orderId],
          recipientEmail: order?.user?.email || "",
          showSendForm: true,
          loading: false,
          error: null,
          success: null,
        },
      }));
    } else {
      setInvoiceForms((prev) => ({
        ...prev,
        [orderId]: {
          ...prev[orderId],
          showSendForm: false,
          error: null,
          success: null,
        },
      }));
    }
  };

  const updateInvoiceForm = (orderId, value) => {
    setInvoiceForms((prev) => ({
      ...prev,
      [orderId]: {
        ...prev[orderId],
        recipientEmail: value,
      },
    }));
  };

  const downloadInvoice = async (order) => {
    try {
      setInvoiceForms((prev) => ({
        ...prev,
        [order.id]: {
          ...prev[order.id],
          loading: true,
          error: null,
          success: null,
        },
      }));

      const token = localStorage.getItem("adminToken");
      const response = await fetch(`${API_BASE_URL}/orders/${order.id}/invoice`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to load invoice");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `Invoice_${order.orderNumber}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);

      setInvoiceForms((prev) => ({
        ...prev,
        [order.id]: {
          ...prev[order.id],
          loading: false,
          success: "Invoice downloaded successfully.",
        },
      }));
    } catch (err) {
      setInvoiceForms((prev) => ({
        ...prev,
        [order.id]: {
          ...prev[order.id],
          loading: false,
          error: err.message,
        },
      }));
    }
  };

  const sendInvoice = async (orderId) => {
    const form = invoiceForms[orderId] || {};
    const recipientEmail = (form.recipientEmail || "").trim();
    const order = orders.find((o) => o.id === orderId);

    if (!recipientEmail) {
      setInvoiceForms((prev) => ({
        ...prev,
        [orderId]: {
          ...prev[orderId],
          error: "Please enter a recipient email.",
          success: null,
        },
      }));
      return;
    }

    try {
      setInvoiceForms((prev) => ({
        ...prev,
        [orderId]: {
          ...prev[orderId],
          loading: true,
          error: null,
          success: null,
        },
      }));

      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${API_BASE_URL}/orders/${orderId}/invoice/send`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ recipientEmail }),
        },
      );

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || "Failed to send invoice email");
      }

      setInvoiceForms((prev) => ({
        ...prev,
        [orderId]: {
          ...prev[orderId],
          loading: false,
          success: "Invoice sent successfully.",
          error: null,
        },
      }));
    } catch (err) {
      setInvoiceForms((prev) => ({
        ...prev,
        [orderId]: {
          ...prev[orderId],
          loading: false,
          error: err.message,
          success: null,
        },
      }));
    }
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (isLoading) {
    return (
      <div className="order-management">
        <div className="loading">Loading orders...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="order-management">
        <div className="error">
          <p>Error: {error}</p>
          <button onClick={fetchOrders}>Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="order-management">
      <div className="order-header">
        <h1>
          <FiBox /> Order Management
        </h1>
        <div className="header-actions">
          <button onClick={fetchOrders} disabled={isLoading}>
            <FiRefreshCw /> Refresh
          </button>
          <button onClick={onLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </div>

      <div className="filters">
        <div className="order-search-box">
          <FiSearch />
          <input
            type="text"
            placeholder="Search by order number, user ID, or transaction ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <FiFilter />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Succeeded">Succeeded</option>
            <option value="Shipped">Shipped</option>
            <option value="Delivered">Delivered</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
        <div className="filter-group">
          <select
            value={orderTypeFilter}
            onChange={(e) => setOrderTypeFilter(e.target.value)}
          >
            <option value="all">All Order Types</option>
            <option value="guest">👤 Guest Orders</option>
            <option value="collect">🏪 Click & Collect</option>
            <option value="regular">✓ Registered Users</option>
          </select>
        </div>
      </div>

      <div className="orders-stats">
        <div className="stat-card">
          <h3>Total Orders</h3>
          <p>{orders.length}</p>
        </div>
        <div className="stat-card">
          <h3>Succeeded</h3>
          <p>{orders.filter((o) => o.status === "Succeeded").length}</p>
        </div>
        <div className="stat-card">
          <h3>Shipped</h3>
          <p>{orders.filter((o) => o.status === "Shipped").length}</p>
        </div>
        <div className="stat-card">
          <h3>Pending</h3>
          <p>{orders.filter((o) => o.status === "Pending").length}</p>
        </div>
        <div className="stat-card">
          <h3>Guest Orders</h3>
          <p>{orders.filter((o) => o.isGuestOrder).length}</p>
        </div>
        <div className="stat-card">
          <h3>Click & Collect</h3>
          <p>{orders.filter((o) => o.isConfirmAndCollect).length}</p>
        </div>
      </div>

      {selectedOrder ? (
        <OrderDetail
          order={selectedOrder}
          onBack={() => setSelectedOrder(null)}
          onStatusUpdate={updateOrderStatus}
          isUpdating={isUpdating}
        />
      ) : (
        <>
          <div className="orders-table">
            <table>
              <thead>
                <tr>
                  <th>Order Number</th>
                  <th>User</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Payment</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentOrders.length > 0 ? (
                  currentOrders.map((order) => (
                    <React.Fragment key={order.id}>
                      <tr>
                        <td>{order.orderNumber}</td>
                        <td>{order.user.firstName}</td>
                        <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                        <td>
                          <div className="order-type-indicators">
                            {order.isGuestOrder && (
                              <span title="Guest Order">👤</span>
                            )}
                            {order.isConfirmAndCollect && (
                              <span title="Click & Collect">🏪</span>
                            )}
                            {!order.isGuestOrder &&
                              !order.isConfirmAndCollect && (
                                <span title="Registered User">✓</span>
                              )}
                          </div>
                        </td>
                        <td>${order.totalAmount?.toFixed(2)}</td>
                        <td>
                          <span
                            className={`status-badge ${order.status.toLowerCase()}`}
                          >
                            {order.status}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`payment-status ${order.paymentStatus?.toLowerCase() || "unknown"}`}
                          >
                            {order.paymentStatus || "N/A"}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn-view"
                            onClick={() => setSelectedOrder(order)}
                          >
                            <FiEye /> Details
                          </button>
                          <button
                            className="btn-action btn-invoice"
                            onClick={() => downloadInvoice(order)}
                            disabled={invoiceForms[order.id]?.loading}
                          >
                            <FiFileText /> Download Invoice
                          </button>
                          {/* <button
                            className="btn-action btn-send-invoice"
                            onClick={() => toggleInvoiceForm(order.id, order)}
                          >
                            <FiMail /> Send Invoice
                          </button> */}
                          {!order.isConfirmAndCollect && (
                            order.isShippingEmailSent ? (
                              <button
                                onClick={() => toggleShippingForm(order.id, order)}
                                className="btn-action btn-shipped"
                              >
                                <FiCheckCircle /> Shipped
                              </button>
                            ) : (
                              <button
                                className="btn-action"
                                onClick={() => toggleShippingForm(order.id, order)}
                              >
                                <FiTruck /> Ship
                              </button>
                            )
                          )}
                        </td>
                      </tr>
                      {shippingForms[order.id]?.showForm && (
                        <tr className="shipping-form-row">
                          <td colSpan="8">
                            <div className="shipping-form">
                              <div className="shipping-form-row-fields">
                                <label htmlFor={`tracking-${order.id}`}>
                                  Tracking Number
                                </label>
                                <input
                                  id={`tracking-${order.id}`}
                                  type="text"
                                  value={shippingForms[order.id]?.trackingNumber || ""}
                                  onChange={(e) =>
                                    updateShippingForm(
                                      order.id,
                                      "trackingNumber",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="Enter tracking number"
                                />
                              </div>
                              <div className="shipping-form-row-fields">
                                <label htmlFor={`service-${order.id}`}>
                                  Shipping Provider
                                </label>
                                <select
                                  id={`service-${order.id}`}
                                  value={shippingForms[order.id]?.shippingService || ""}
                                  onChange={(e) =>
                                    updateShippingForm(
                                      order.id,
                                      "shippingService",
                                      e.target.value,
                                    )
                                  }
                                >
                                  <option value="">Select a carrier</option>
                                  {shippingOptions.map((service) => (
                                    <option key={service} value={service}>
                                      {service}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="shipping-form-actions">
                                <button
                                  className="btn-submit"
                                  onClick={() => submitShippingInfo(order.id)}
                                  disabled={shippingForms[order.id]?.loading}
                                >
                                  {shippingForms[order.id]?.loading
                                    ? "Sending..."
                                    : "Send Shipping Info"}
                                </button>
                                <button
                                  className="btn-cancel"
                                  onClick={() => toggleShippingForm(order.id, order)}
                                  type="button"
                                >
                                  Cancel
                                </button>
                              </div>
                              {shippingForms[order.id]?.error && (
                                <p className="error-message">
                                  {shippingForms[order.id]?.error}
                                </p>
                              )}
                              {shippingForms[order.id]?.success && (
                                <p className="success-message">
                                  {shippingForms[order.id]?.success}
                                </p>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                      {invoiceForms[order.id]?.showSendForm && (
                        <tr className="invoice-form-row">
                          <td colSpan="8">
                            <div className="invoice-form">
                              <div className="invoice-form-row-fields">
                                <label htmlFor={`recipient-${order.id}`}>
                                  Recipient Email
                                </label>
                                <input
                                  id={`recipient-${order.id}`}
                                  type="email"
                                  value={invoiceForms[order.id]?.recipientEmail || ""}
                                  onChange={(e) =>
                                    updateInvoiceForm(order.id, e.target.value)
                                  }
                                  placeholder="Enter recipient email"
                                />
                              </div>
                              <div className="shipping-form-actions">
                                <button
                                  className="btn-submit"
                                  onClick={() => sendInvoice(order.id)}
                                  disabled={invoiceForms[order.id]?.loading}
                                >
                                  {invoiceForms[order.id]?.loading
                                    ? "Sending..."
                                    : "Send Invoice"}
                                </button>
                                <button
                                  className="btn-cancel"
                                  onClick={() => toggleInvoiceForm(order.id, order)}
                                  type="button"
                                >
                                  Cancel
                                </button>
                              </div>
                              {invoiceForms[order.id]?.error && (
                                <p className="error-message">
                                  {invoiceForms[order.id]?.error}
                                </p>
                              )}
                              {invoiceForms[order.id]?.success && (
                                <p className="success-message">
                                  {invoiceForms[order.id]?.success}
                                </p>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="no-orders">
                      No orders found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <FiArrowLeft /> Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <button
                    key={page}
                    onClick={() => paginate(page)}
                    className={currentPage === page ? "active" : ""}
                  >
                    {page}
                  </button>
                ),
              )}

              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next <FiArrowRight />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Order Detail Component
const OrderDetail = ({ order, onBack, onStatusUpdate, isUpdating }) => {
  const [newStatus, setNewStatus] = useState(order.status);
  const [recipientEmail, setRecipientEmail] = useState(order.user?.email || order.shippingAddress?.email || "");
  const [invoiceStatusMessage, setInvoiceStatusMessage] = useState(null);
  const [invoiceError, setInvoiceError] = useState(null);
  const [isSendingInvoice, setIsSendingInvoice] = useState(false);
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);

  const handleStatusUpdate = () => {
    if (newStatus !== order.status) {
      onStatusUpdate(order.id, newStatus);
    }
  };

  const handleDownloadInvoice = async () => {
    try {
      setInvoiceError(null);
      setInvoiceStatusMessage(null);
      setIsDownloadingInvoice(true);

      const token = localStorage.getItem("adminToken");
      const response = await fetch(`${API_BASE_URL}/orders/${order.id}/invoice`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to download invoice");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `Invoice_${order.orderNumber}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);

      setInvoiceStatusMessage("Invoice downloaded successfully.");
    } catch (err) {
      setInvoiceError(err.message);
    } finally {
      setIsDownloadingInvoice(false);
    }
  };

  const handleSendInvoice = async () => {
    const email = (recipientEmail || "").trim();
    if (!email) {
      setInvoiceError("Please enter a recipient email.");
      setInvoiceStatusMessage(null);
      return;
    }

    try {
      setInvoiceError(null);
      setInvoiceStatusMessage(null);
      setIsSendingInvoice(true);

      const token = localStorage.getItem("adminToken");
      const response = await fetch(`${API_BASE_URL}/orders/${order.id}/invoice/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ recipientEmail: email }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.message || "Failed to send invoice email");
      }

      setInvoiceStatusMessage("Invoice sent successfully.");
    } catch (err) {
      setInvoiceError(err.message);
    } finally {
      setIsSendingInvoice(false);
    }
  };

  const hasOrderRequiredItems = () => {
    return order.items?.some((item) => item.isOrderRequired === true) || false;
  };

  return (
    <div className="order-detail">
      <button className="back-button" onClick={onBack}>
        <FiArrowLeft /> Back to Orders
      </button>

      <div className="order-detail-header">
        <h2>Order #{order.orderNumber}</h2>
        {hasOrderRequiredItems() && (
          <div className="order-required-ribbon">
            <FiAlertCircle /> Contains items that require special ordering
          </div>
        )}
        <div className="order-status-control">
          <select
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
            disabled={isUpdating}
          >
            <option value="Pending">Pending</option>
            <option value="Succeeded">Succeeded</option>
            <option value="Shipped">Shipped</option>
            <option value="Delivered">Delivered</option>
            <option value="Cancelled">Cancelled</option>
          </select>
          <button
            onClick={handleStatusUpdate}
            disabled={newStatus === order.status || isUpdating}
          >
            {isUpdating ? "Updating..." : "Update Status"}
          </button>
        </div>
      </div>

      <div className="invoice-actions">
        <button
          className="btn-action btn-invoice"
          onClick={handleDownloadInvoice}
          disabled={isDownloadingInvoice}
        >
          <FiFileText />
          {isDownloadingInvoice ? "Downloading..." : " Download Invoice"}
        </button>
        {/* <div className="invoice-send-box">
          <input
            type="email"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            placeholder="Recipient email"
          />
          <button
            className="btn-action btn-send-invoice"
            onClick={handleSendInvoice}
            disabled={isSendingInvoice || !recipientEmail.trim()}
          >
            <FiMail />
            {isSendingInvoice ? "Sending..." : " Send Invoice"}
          </button>
        </div> */}
      </div>
      {invoiceError && <p className="error-message">{invoiceError}</p>}
      {invoiceStatusMessage && (
        <p className="success-message">{invoiceStatusMessage}</p>
      )}

      <div className="order-detail-grid">
        <div className="order-info">
          <h3>Order Information</h3>
          <div className="info-group">
            <label>Order Date:</label>
            <span>{new Date(order.createdAt).toLocaleString()}</span>
          </div>
          <div className="info-group">
            <label>Status:</label>
            <span className={`status-badge ${order.status.toLowerCase()}`}>
              {order.status}
            </span>
          </div>
          <div className="info-group">
            <label>Payment Status:</label>
            <span
              className={`payment-status ${order.paymentStatus?.toLowerCase() || "unknown"}`}
            >
              {order.paymentStatus || "N/A"}
            </span>
          </div>
          <div className="info-group">
            <label>Transaction ID:</label>
            <span>{order.transactionId || "N/A"}</span>
          </div>
          <div className="info-group">
            <label>Total Amount:</label>
            <span>${order.totalAmount?.toFixed(2)}</span>
          </div>
        </div>

        <div className="shipping-info">
          <h3>Shipping Information</h3>
          {order.shippingAddress ? (
            <>
              <div className="info-group">
                <label>Name:</label>
                <span>{order.shippingAddress.fullName}</span>
              </div>
              <div className="info-group">
                <label>Phone:</label>
                <span>{order.shippingAddress.phoneNumber}</span>
              </div>
              <div className="info-group">
                <label>Email:</label>
                <span>{order.user.email}</span>
              </div>
              <div className="info-group">
                <label>Address:</label>
                <span>
                  {order.shippingAddress.addressLine1}
                  <br />
                  {order.shippingAddress.addressLine2 && (
                    <>
                      {order.shippingAddress.addressLine2}
                      <br />
                    </>
                  )}
                  {order.shippingAddress.city}, {order.shippingAddress.state}{" "}
                  {order.shippingAddress.postalCode}
                  <br />
                  {order.shippingAddress.country}
                </span>
              </div>
            </>
          ) : (
            <p>No shipping information available</p>
          )}
        </div>

        <div className="order-items">
          <h3>Order Items</h3>
          {order.items && order.items.length > 0 ? (
            <div className="items-list">
              {order.items.map((item) => (
                <div key={item.id} className="order-item">
                  <div className="item-image">
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt={item.productName} />
                    ) : (
                      <div className="image-placeholder">
                        <FiBox />
                      </div>
                    )}
                  </div>
                  <div className="item-details">
                    <h4>{item.productName}</h4>
                    <p>Product ID: {item.productId}</p>
                    <p>Quantity: {item.quantity}</p>
                    <p>Price: ${item.unitPrice?.toFixed(2)} each</p>
                    {item.isOrderRequired && (
                      <p className="item-required-note">
                        ⚠️ This item must be ordered from supplier
                      </p>
                    )}
                  </div>
                  <div className="item-total">
                    ${(item.unitPrice * item.quantity).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p>No items found for this order</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderManagement;
